import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Overview from "./pages/Overview";
import EmployeeKitchen from "./pages/EmployeeKitchen";
import OrdersHub from "./pages/OrdersHub";
import Catalog from "./pages/Catalog";
import Activity from "./pages/Activity";
import DailyReportDetail from "./pages/DailyReportDetail";
import Invoices from "./pages/Invoices";
import InvoiceDetail from "./pages/InvoiceDetail";
import Users from "./pages/Users";
import MyInventory from "./pages/MyInventory";
import DailyReport from "./pages/DailySoldLog";
import OpeningInventory from "./pages/OpeningInventory";
import PrintableKitchenExtract from "./pages/PrintableKitchenExtract";
import VersionBanner from "./components/VersionBanner";

const ALL = ["admin", "franchisee", "employee"];
const ADMIN = ["admin"];
const ADMIN_OR_FRANCHISEE = ["admin", "franchisee"];

// Hoisted to module scope so identity is stable across renders (no useless re-renders of Toaster)
const TOAST_OPTIONS = {
  style: {
    fontFamily: "'Inter', sans-serif",
    borderRadius: "0.75rem",
  },
};

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Overview />;
  if (user.role === "franchisee") return <Navigate to="/orders?tab=place" replace />;
  return <Navigate to="/kitchen" replace />;
}

function KitchenRouter() {
  // Admin, kitchen employees and warehouse locations only — store logins go to Place Order.
  const { user } = useAuth();
  if (user?.role === "franchisee" && !user?.warehouse) return <Navigate to="/orders?tab=place" replace />;
  return <EmployeeKitchen />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster
          position="bottom-right"
          richColors
          closeButton
          toastOptions={TOAST_OPTIONS}
        />
        <BrowserRouter>
          <VersionBanner />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />

            <Route path="/kitchen" element={<ProtectedRoute allow={["admin", "employee", "franchisee"]}><KitchenRouter /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute allow={ALL}><OrdersHub /></ProtectedRoute>} />
            <Route path="/my-orders" element={<Navigate to="/orders?tab=mine" replace />} />
            <Route path="/catalog" element={<ProtectedRoute allow={ADMIN}><Catalog /></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute allow={ADMIN_OR_FRANCHISEE}><Invoices /></ProtectedRoute>} />
            <Route path="/invoices/:id" element={<ProtectedRoute allow={ADMIN_OR_FRANCHISEE}><InvoiceDetail /></ProtectedRoute>} />
            <Route path="/activity" element={<ProtectedRoute allow={["admin", "franchisee", "employee"]}><Activity /></ProtectedRoute>} />
            <Route path="/activity/reports/:id" element={<ProtectedRoute allow={ALL}><DailyReportDetail /></ProtectedRoute>} />
            <Route path="/team" element={<ProtectedRoute allow={ADMIN}><Users /></ProtectedRoute>} />
            <Route path="/my-inventory" element={<ProtectedRoute allow={["franchisee", "admin"]}><MyInventory /></ProtectedRoute>} />
            <Route path="/daily-sold" element={<Navigate to="/daily-report" replace />} />
            <Route path="/daily-report" element={<ProtectedRoute allow={["franchisee", "admin"]}><DailyReport /></ProtectedRoute>} />
            <Route path="/admin/opening-inventory" element={<ProtectedRoute allow={ADMIN}><OpeningInventory /></ProtectedRoute>} />
            <Route path="/print/kitchen/:id" element={<ProtectedRoute allow={ADMIN}><PrintableKitchenExtract /></ProtectedRoute>} />

            {/* Backward-compat aliases */}
            <Route path="/location" element={<Navigate to="/kitchen" replace />} />
            <Route path="/location/reports" element={<Navigate to="/activity?tab=daily-reports" replace />} />
            <Route path="/location/reports/:id" element={<RedirectReport />} />
            <Route path="/commissary" element={<Navigate to="/kitchen" replace />} />
            <Route path="/franchise" element={<Navigate to="/orders?tab=place" replace />} />
            <Route path="/packaging" element={<Navigate to="/orders?tab=packaging" replace />} />
            <Route path="/users" element={<Navigate to="/team" replace />} />
            <Route path="/notifications" element={<Navigate to="/activity" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

function RedirectReport() {
  // Bridge old /location/reports/:id → /activity/reports/:id
  const path = window.location.pathname.split("/").pop();
  return <Navigate to={`/activity/reports/${path}`} replace />;
}

export default App;
